/**
 * capSceneTextures — enforce the active tier's `maxTextureRes` on a freshly
 * parsed GLB scene, and MEASURE what actually stays resident.
 *
 * KTX2/Basis textures transcode on a worker, but every mip level still uploads
 * on the MAIN thread at first bind (compressedTexSubImage2D) — profiled at
 * 464 MB / 48.5 s of blocking across a 75 s potato city walk (2026-07-30, the
 * walk-2s/freeze-2s report). Dropping the top levels of an already-transcoded
 * mip chain costs nothing visually on a low tier — the GPU would never sample
 * level 0 of a 2048² texture under a 512 budget — and cuts upload bytes 4× per
 * dropped level. Textures at or under the cap (and single-level chains, which
 * cannot be resized without a re-transcode) pass through untouched.
 *
 * The returned `measuredMb` (texture bytes kept + geometry attribute bytes) is
 * the cache's VRAM metric. The cooker's `vram_estimate_mb` derives from LOD0
 * full-res textures, which over-estimated LOD1 variants ~20× — the potato
 * 128 MB budget then evicted every unreferenced master at 60 Hz, turning chunk
 * churn into a refetch+re-upload loop (the cache-thrash half of the stutter).
 */
import type * as THREE from 'three/webgpu';

interface MipLevel { width: number; height: number; data?: { byteLength?: number } }

interface TextureLike {
  isTexture?: boolean;
  isCompressedTexture?: boolean;
  uuid: string;
  mipmaps?: MipLevel[];
  image?: { width?: number; height?: number };
  userData?: Record<string, unknown>;
}

export interface TextureBudgetReport {
  texturesSeen: number;
  capped: number;
  measuredMb: number;
}

/** A texture whose top levels were cut at parse and can be promoted later (mips-first uploads). */
export interface RetainedTop { texture: THREE.Texture; topMips: Array<{ width: number; height: number; data?: ArrayBufferView }> }

export function capSceneTextures(root: THREE.Object3D, maxRes: number, retain?: RetainedTop[]): TextureBudgetReport {
  const seenGeom = new Set<string>();
  const seenTex = new Set<string>();
  // Dedup geometry bytes by UNDERLYING ArrayBuffer: interleaved attributes
  // (position+normal+uv sharing one InterleavedBuffer) otherwise count the
  // whole buffer once PER attribute — a 3-4× VRAM inflation that would make
  // the budget evict early and soft-reintroduce the thrash this measurement
  // exists to fix.
  const seenBuf = new Set<ArrayBufferLike>();
  const addArray = (a: ArrayBufferView | undefined): void => {
    if (!a || seenBuf.has(a.buffer)) return;
    seenBuf.add(a.buffer);
    bytes += a.byteLength;
  };
  let bytes = 0;
  let capped = 0;
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    const geom = mesh.geometry as THREE.BufferGeometry | undefined;
    if (geom && !seenGeom.has(geom.uuid)) {
      seenGeom.add(geom.uuid);
      for (const attr of Object.values(geom.attributes)) {
        addArray((attr as THREE.BufferAttribute).array as ArrayBufferView | undefined);
      }
      addArray(geom.index?.array as ArrayBufferView | undefined);
    }
    const mats = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
    for (const mat of mats) {
      for (const v of Object.values(mat as unknown as Record<string, unknown>)) {
        const t = v as TextureLike | null | undefined;
        if (!t || !t.isTexture || seenTex.has(t.uuid)) continue;
        seenTex.add(t.uuid);
        if (t.isCompressedTexture && Array.isArray(t.mipmaps)) {
          if (t.mipmaps.length > 1 && t.userData?.__mipCapped !== true) {
            let drop = 0;
            while (drop < t.mipmaps.length - 1 && Math.max(t.mipmaps[drop].width, t.mipmaps[drop].height) > maxRes) drop++;
            if (drop > 0) {
              const dropped = t.mipmaps.slice(0, drop);
              if (retain) retain.push({ texture: t as unknown as THREE.Texture, topMips: dropped as RetainedTop['topMips'] });
              t.mipmaps.splice(0, drop);
              if (t.image) { t.image.width = t.mipmaps[0].width; t.image.height = t.mipmaps[0].height; }
              capped++;
            }
            if (t.userData) t.userData.__mipCapped = true;
          }
          for (const m of t.mipmaps) bytes += m.data?.byteLength ?? 0;
        } else {
          // Uncompressed (rare in cooked GLBs): RGBA + ~1/3 for generated mips.
          bytes += (t.image?.width ?? 0) * (t.image?.height ?? 0) * 4 * 1.33;
        }
      }
    }
  });
  return { texturesSeen: seenTex.size, capped, measuredMb: bytes / 1048576 };
}
